import numeral from "numeral";
import React, { useState } from "react";
import { Button, Card, Form, Modal, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import usePalette from "../../../../hooks/usePalette";
import Select, { components } from "react-select";
import { customStylesForm } from "../../../../components/common/customStyleSelect";
import CurrencyInput from "react-currency-input-field";
import { isEmpty, isNull } from "lodash";

import Icon from "@mdi/react";
import { mdiLoading } from "@mdi/js";

import { addNewProjectScope } from "../../../../redux/slices/certify/projectsSlice";

const NewProjectScope = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const { project, projectScopeItems, ceilingFlowState } = useSelector(
    (state) => ({
      project: state.certifyReducer.project.data,
      projectScopeItems: state.utilsReducer.dropdownItems.projectScopeItems,
      ceilingFlowState: state.utilsReducer.certifyFinancial,
    }),
  );

  const [costDescriptionValue, setCostDescriptionValue] = useState(null);
  const [costRequiredValue, setCostRequiredValue] = useState("");
  const [ceilingValue, setCeilingValue] = useState("");
  const [errors, setErrors] = useState({
    costDescription: false,
    costRequired: false,
    ceiling: false,
    costLowerThanCeiling: false,
  });
  const [btnLoading, setBtnLoading] = useState(false);

  const resetForm = () => {
    setCostDescriptionValue(null);
    setCostRequiredValue("");
    setCeilingValue("");
    setErrors({
      costDescription: false,
      costRequired: false,
      ceiling: false,
      costLowerThanCeiling: false,
    });
  };

  const handleOnChange = (value) => {
    setCostDescriptionValue(value);
    setErrors((prev) => ({ ...prev, costDescription: false }));
  };

  const validateForm = () => {
    const newErrors = {
      costDescription: !costDescriptionValue,
      costRequired: !costRequiredValue,
      ceiling: !ceilingValue,
      costLowerThanCeiling:
        parseFloat(costRequiredValue || 0) < parseFloat(ceilingValue || 0),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleAddProjectScope = async () => {
    if (!validateForm()) {
      return;
    }

    setBtnLoading(true);

    try {
      const props = {
        id: null,
        project_id: project.id,
        cost_description: costDescriptionValue.value,
        cost_required: costRequiredValue,
        revised_cost_required: costRequiredValue,
        ceiling: ceilingValue,
        revised_ceiling: ceilingValue,
        is_rejected: false,
        ceiling_flow_details: [],
      };
      await dispatch(addNewProjectScope(props));

      resetForm();
      onHide();
    } finally {
      setBtnLoading(false);
    }
  };

  const handleClose = () => {
    resetForm(); // Reset form when modal is closed
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='md'
      centered
      keyboard={false}
      backdrop='static'
    >
      <Modal.Body>
        <div className='p-4'>
          <h4 className='mb-3'>Tambah Skop Projek</h4>
          <Form.Group className='mb-3'>
            <Form.Label>Projek Skop</Form.Label>
            <Select
              name='cost_description'
              placeholder='Pilih Skop...'
              className='react-select-container'
              classNamePrefix='react-select'
              isSearchable
              options={projectScopeItems}
              styles={{
                ...customStylesForm,
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                singleValue: (provided) => ({
                  ...provided,
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  maxWidth: "100%",
                  overflow: "visible",
                }),
                control: (provided) => ({
                  ...provided,
                  minHeight: "auto",
                  alignItems: "flex-start",
                  borderColor: errors.costDescription
                    ? "#dc3545"
                    : provided.borderColor,
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: "8px",
                  flexWrap: "wrap",
                }),
                input: (provided) => ({
                  ...provided,
                  padding: 0,
                  margin: 0,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition='absolute'
              value={costDescriptionValue}
              onChange={(value) => handleOnChange(value)}
              noOptionsMessage={() => "Tiada pilihan"}
              components={{
                SingleValue: ({ children, ...props }) => (
                  <components.SingleValue {...props}>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxWidth: "100%",
                      }}
                    >
                      {children}
                    </div>
                  </components.SingleValue>
                ),
              }}
            />
            {errors.costDescription && (
              <Form.Text className='text-danger'>
                Sila pilih skop projek
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>Jumlah Kos Dipohon</Form.Label>
            <Form.Control
              as={CurrencyInput}
              name='cost_required'
              placeholder='0.00'
              decimalsLimit={2}
              decimalScale={2}
              value={costRequiredValue}
              onValueChange={(value) => {
                setCostRequiredValue(value);
                setErrors((prev) => ({
                  ...prev,
                  costRequired: !value,
                  costLowerThanCeiling: false,
                }));
              }}
              isInvalid={errors.costRequired || errors.costLowerThanCeiling}
            />
            {errors.costRequired && (
              <Form.Control.Feedback type='invalid'>
                Sila masukkan jumlah kos
              </Form.Control.Feedback>
            )}
            {errors.costLowerThanCeiling && (
              <Form.Control.Feedback type='invalid'>
                Jumlah kos tidak boleh kurang daripada siling
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>Jumlah Siling Dipohon</Form.Label>
            <Form.Control
              as={CurrencyInput}
              name='ceiling_value'
              placeholder='0.00'
              decimalScale={2}
              decimalsLimit={2}
              value={ceilingValue}
              onValueChange={(value) => {
                setCeilingValue(value);
                setErrors((prev) => ({
                  ...prev,
                  ceiling: !value,
                  costLowerThanCeiling: false,
                }));
              }}
              isInvalid={errors.ceiling}
            />
            {errors.ceiling && (
              <Form.Control.Feedback type='invalid'>
                Sila masukkan jumlah siling
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </div>
        <div className='d-flex justify-content-end gap-3 px-4'>
          <Button variant='light' onClick={handleClose}>
            Batal
          </Button>
          <Button
            className='px-3'
            variant='success'
            onClick={handleAddProjectScope}
            disabled={btnLoading}
          >
            {btnLoading ? (
              <Icon className='mx-3' path={mdiLoading} size={0.8} spin={true} />
            ) : (
              <span style={{ marginLeft: 2.5, marginRight: 2.5 }}>Simpan</span>
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default NewProjectScope;
